import { correctedNow } from '@app/utils/clockOffset'
import { createSelector } from '@app/utils/createSelector'
import { captureException } from '@app/utils/errorReport/errorReport'
import { createLogger } from '@app/utils/loggerCreator'

import { ChatServerManager, type ChatServerManagerOptions } from '@app/packages/chat/Manager'
import { withDisposableStack } from '@app/packages/disposable/disposable'
import { LazyPromise } from '@app/packages/LazyPromise/LazyPromise'
import type { TrackableValue } from '@app/packages/reactives/TrackableValue'
import { unwrapLoadableState } from '@app/packages/thunkSelector/thunkSelector'

import { getChatToken } from '@app/store/actions/chatToken'
import { profileChatUidSelector } from '@app/store/selectors/profile'
import type { ChatConfig } from '@app/store/slices/app_config'
import { applyChatAction, setOwner } from '@app/store/slices/chat/actions'
import { chatChannelsSelector, chatTotalUnreadSelector } from '@app/store/slices/chat/selectors'
import { configSelector } from '@app/store/slices/config'
import { hasGuestChatSessionSelector } from '@app/store/slices/guest_chat_session'
import { localeSelector } from '@app/store/slices/locale'
import { isSupervisedSelector, v3SessionSelector } from '@app/store/slices/session'
import { hasSupportChatSessionSelector } from '@app/store/slices/support_chat_session'
import { setUnreadCountSlice } from '@app/store/slices/unreadCount'
import { type Store, waitForMappedValue } from '@app/store/store'
import { createThunk } from '@app/store/thunk'

import { getActiveChannelsSet } from './activeChannels'
import { getAuthSession } from './authSession'
import { getChatApi } from './chatApi'
import { getErrorReportController } from './errorReportController'

/** Construct the chat_server manager wired to the store, ready to `start()`. */
export function createChatManager(store: Store, config: ChatConfig, foreground: TrackableValue<boolean>): ChatServerManager {
  return new ChatServerManager(chatManagerOptions(store, config, foreground))
}

/** Store bindings the package `ChatServerManager` needs: socket wiring plus the Kidsout lifecycle policy. */
export function chatManagerOptions(store: Store, config: ChatConfig, foreground: TrackableValue<boolean>): ChatServerManagerOptions {
  const log = createLogger('ChatServerManager', 'orange', 'bold')
  const loadingChannels = new Set<string>()
  const errorReporter = store.dispatch(getErrorReportController())
  const reportError = (error: unknown, op: string) => {
    const err = errorReporter.errorForCapture(error)
    if (err) captureException(err, { tags: { feature: 'chat_server', chat_op: op } })
  }

  const notify = new LazyPromise(async signal => {
    const { NotificationSound } = await import('@app/utils/NotificationSound')
    signal.throwIfAborted()
    return new NotificationSound()
  })

  return {
    getWsUrl: () => config.wsUrl,
    getToken: () => store.dispatch(getChatToken()),
    peekToken: () => store.dispatch(getAuthSession()).peekAccessToken(),
    serverNow: correctedNow,
    getLocale: () => localeSelector(store.getState()),
    getSelfUid: () => profileChatUidSelector(store.getState()),
    foreground,
    dispatch: action => {
      store.dispatch(applyChatAction(action))
    },
    log: (...args) => log.log(...args),
    reportError: (error, { op }) => reportError(error, op),
    // A doubt never hides a failure: only a definite "offline" keeps a failed connect out of the count.
    isOnline: () => globalThis.navigator?.onLine !== false,

    waitUntilReady: signal => waitChatAvailable(store, signal),

    shouldConnect: () => {
      const state = store.getState()
      return !(isSupervisedSelector(state) || configSelector(state).isPolygon)
    },

    register: manager => {
      return withDisposableStack(stack => {
        store.dispatch(setChatManager(manager))
        stack.defer(() => store.dispatch(unsetChatManager()))
        // Bind the cache to the account it belongs to. Switching accounts without a
        // reload (the staging test-user switcher, supervision) rotates the chat uid
        // while this manager keeps running — the previous user's channels must not
        // survive into the new session, or `/m/support` resolves a channel the new
        // token has no access to (FRONTEND-90E).
        let lastOwner = profileChatUidSelector(store.getState())
        store.dispatch(setOwner(lastOwner))
        stack.defer(
          store.subscribe(() => {
            const uid = profileChatUidSelector(store.getState())
            if (uid === lastOwner) return
            lastOwner = uid
            store.dispatch(setOwner(uid))
          })
        )
        // Adopt session-token rotations proactively: when the access token changes
        // (local refresh, 401 force-refresh, bfcache invalidate, or a peer tab),
        // prolong the live socket's auth in-band (`auth:refresh`) — no reconnect,
        // no presence flap; the client itself falls back to a reconnect when the
        // push can't go through.
        let lastToken = v3SessionSelector(store.getState())?.access_token ?? null
        stack.defer(
          store.subscribe(() => {
            const token = v3SessionSelector(store.getState())?.access_token ?? null
            if (token === lastToken) return
            lastToken = token
            if (token && manager.isConnected) void manager.refreshAuth().catch(err => reportError(err, 'refreshAuthOnTokenChange'))
          })
        )
      })
    },

    // Publish the total unread for the nav badge and page title; preload the
    // notification sound.
    onActivate: () => {
      return withDisposableStack(stack => {
        void notify.getValue().catch(() => {})
        let lastUnread = chatTotalUnreadSelector(store.getState())
        store.dispatch(setUnreadCountSlice(lastUnread))
        stack.defer(() => store.dispatch(setUnreadCountSlice(0)))
        stack.defer(
          store.subscribe(() => {
            const next = chatTotalUnreadSelector(store.getState())
            if (next !== lastUnread) {
              lastUnread = next
              store.dispatch(setUnreadCountSlice(next))
            }
          })
        )
      })
    },

    loadInitialChannels: async signal => {
      const externalId = profileChatUidSelector(store.getState())
      if (externalId === null || externalId === undefined) return
      signal.throwIfAborted()
      await store.dispatch(getChatApi()).loadUserChannels(String(externalId))
    },

    ensureChannelLoaded: async channelId => {
      if (chatChannelsSelector(store.getState())[channelId]) return
      if (loadingChannels.has(channelId)) return
      loadingChannels.add(channelId)
      try {
        await store.dispatch(getChatApi()).loadChannel(channelId)
      } finally {
        loadingChannels.delete(channelId)
      }
    },

    refreshChannel: async channelId => {
      if (!chatChannelsSelector(store.getState())[channelId]) return
      await store.dispatch(getChatApi()).loadChannel(channelId)
    },

    isChannelActive: channelId => store.dispatch(getActiveChannelsSet()).has(channelId),

    onNotify: () => {
      void notify
        .getValue()
        .then(sound => sound.play())
        .catch(() => {})
    },
  }
}

export function getChatManager() {
  return createThunk((_dispatch, _getState, context) => context.lookupEnsure<ChatServerManager>(ChatManagerSymbol))
}

export function getChatManagerSafe() {
  return createThunk((_dispatch, _getState, context) => context.lookup<ChatServerManager>(ChatManagerSymbol) ?? null)
}

export function setChatManager(value: ChatServerManager) {
  return createThunk((_dispatch, _getState, context) => context.getRootContext().set(ChatManagerSymbol, value))
}

export function unsetChatManager() {
  return createThunk((_dispatch, _getState, context) => context.getRootContext().unset(ChatManagerSymbol))
}

const ChatManagerSymbol = Symbol('ChatServerManager')

/** Resolve once chat is available: anyone with a raised support session, else account-type / sitter-onboarding gated. */
async function waitChatAvailable(store: Store, signal: AbortSignal): Promise<void> {
  const { profileUserResultSelector } = await import('@app/store/selectors/profile')
  const { sitterCandidateRoutingResultSelector } = await import('@app/store/selectors/sitter_onboarding')
  const { createLoadable } = await import('@app/packages/thunkSelector/thunkSelector')

  const shouldLoadResultSelector = createSelector(
    [profileUserResultSelector, sitterCandidateRoutingResultSelector],
    (userResult, sitterCandidateRoutingResult) =>
      createLoadable(c => {
        const user = c.unwrap(userResult)
        if (!user || user.account_type === 'visitor') return false
        if (user.account_type === 'parent') return user.registration_completed
        if (user.account_type === 'sitter') {
          if (user.approved) return true
          const sitterCandidateRouting = c.unwrap(sitterCandidateRoutingResult)
          return !!sitterCandidateRouting?.messages_available
        }
        return false
      }),
    { cache: {} }
  )

  const shouldLoadSelector = createSelector(
    [shouldLoadResultSelector, hasGuestChatSessionSelector, hasSupportChatSessionSelector],
    (val, guest, support) => {
      // A raised support session is ready by definition: a guest has no profile to
      // gate on, and the signed-in ones sent here are exactly whom the gate rejects
      // (suspended sitters, candidates without messages).
      if (guest || support) return true
      if (val.error) return false
      return !!unwrapLoadableState(val.value)
    },
    { cache: {} }
  )

  await waitForMappedValue(store, shouldLoadSelector, val => (val ? { val: true } : null), signal)
}
